import { useEffect, type MutableRefObject } from 'react';
import type { PredicateInfo } from '@/entities/search';
import type { CommittedSearch } from './useCommittedSearch';

type OnSearchChange = (val: { human: string; dsl: string }) => void;

type UsePredicateSyncParams = {
  predicateInfo: PredicateInfo | null;
  committedSearch: CommittedSearch | null;
  onSearchChange: OnSearchChange;
  /** For NL mode: cache resolved DSL to avoid re-conversion on pagination */
  resolvedDslRef?: MutableRefObject<string | null>;
};

/**
 * Syncs predicate DSL/dslHuman from API response back to search input.
 * When NL search returns, updates input with canonical dslHuman; caches dsl for pagination.
 */
export const usePredicateSync = ({
  predicateInfo,
  committedSearch,
  onSearchChange,
  resolvedDslRef,
}: UsePredicateSyncParams) => {
  useEffect(() => {
    if (!predicateInfo) return;

    if (predicateInfo.dslHuman && committedSearch?.mode === 'nl') {
      onSearchChange({ human: predicateInfo.dslHuman, dsl: '' });
      if (resolvedDslRef) {
        resolvedDslRef.current = predicateInfo.dsl;
      }
    }

    if (predicateInfo.dsl && committedSearch?.mode === 'dsl') {
      onSearchChange({ dsl: predicateInfo.dsl ?? '', human: '' });
    }
  }, [predicateInfo, committedSearch?.mode, onSearchChange, resolvedDslRef]);
};
